// PreventiApp · Sprint 4 · AnonimizarFoto — vista previa cliente con:
//  1. Detección automática MediaPipe + pixelado irreversible.
//  2. Pincel manual (solo AGREGAR ocultamiento, nunca quitar).
//  3. Botones: Confirmar y guardar / Volver a tomar / Ocultar zona manualmente.
//  4. Preview server-side (tras backend re-pixelar si detecta rostros).
//
// Reglas: la imagen ORIGINAL nunca sale del navegador — sólo el canvas anonimizado.
import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, Brush, RotateCcw, Check, AlertTriangle } from 'lucide-react';
import { detectFaces, pixelateRegion } from '../../lib/faceDetector';
import { supabase } from '../../lib/supabase';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_SIDE = 1600; // limita el lado mayor antes de subir (rendimiento)

export default function AnonimizarFoto({ file, modo, onCancel, onDone }) {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState('loading');    // loading | ready | brushing | uploading | preview | error
  const [error, setError] = useState('');
  const [faceCount, setFaceCount] = useState(0);
  const [manualBrushSize, setManualBrushSize] = useState(48);
  const [serverPreview, setServerPreview] = useState(null); // { staging_path, preview_url, rostros_servidor, repixelado, pending_server_verification?, pending_data_url? }
  const brushingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setStatus('loading');
        const img = await loadImage(file);
        const { w, h } = fitSize(img, MAX_SIDE);
        const canvas = canvasRef.current;
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        // Detección
        const faces = await detectFaces(canvas).catch((e) => { throw new Error('Detección no disponible en este dispositivo. Usa otra imagen o navega desde un dispositivo compatible. (' + e.message + ')'); });
        if (cancelled) return;
        for (const f of faces) pixelateRegion(ctx, f.x, f.y, f.w, f.h);
        setFaceCount(faces.length);
        setStatus('ready');
      } catch (e) {
        if (!cancelled) { setError(e.message); setStatus('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [file]);

  const applyManualStroke = (evt) => {
    if (!brushingRef.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
    const clientY = evt.touches ? evt.touches[0].clientY : evt.clientY;
    const x = Math.round((clientX - rect.left) * scaleX);
    const y = Math.round((clientY - rect.top) * scaleY);
    const size = manualBrushSize;
    const ctx = canvas.getContext('2d');
    pixelateRegion(ctx, x - size / 2, y - size / 2, size, size, 6);
  };

  const startBrush = (e) => { e.preventDefault(); brushingRef.current = true; applyManualStroke(e); };
  const moveBrush = (e) => { if (brushingRef.current) { e.preventDefault(); applyManualStroke(e); } };
  const endBrush = () => { brushingRef.current = false; };

  const confirmar = async () => {
    try {
      setStatus('uploading');
      // Sube canvas actual al backend (imagen ya pixelada en cliente). Original nunca viaja.
      const blob = await canvasToBlob(canvasRef.current);
      // Si estamos offline, no se puede validar en servidor ahora: guardamos el blob pixelado
      // como dataURL para que el sync posterior lo envíe. La imagen original NUNCA se persiste.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        const pending_data_url = await blobToDataURL(blob);
        setServerPreview({
          pending_server_verification: true,
          pending_data_url,
          rostros_cliente: faceCount,
        });
        setStatus('preview');
        return;
      }
      const fd = new FormData();
      fd.append('file', blob, 'evidencia.jpg');
      fd.append('obs_id', 'pending');
      fd.append('modo_registro', modo || 'operativo');
      fd.append('rostros_cliente', String(faceCount));
      const token = (await supabase.auth.getSession()).data?.session?.access_token;
      const r = await fetch(`${API}/api/evidencia/anonimizar`, {
        method: 'POST', body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setServerPreview({ ...j, rostros_cliente: faceCount });
      setStatus('preview');
    } catch (e) {
      // Fallback: si el backend está caído o la red falló mid-request, guardamos como pending
      try {
        const blob = await canvasToBlob(canvasRef.current);
        const pending_data_url = await blobToDataURL(blob);
        setServerPreview({
          pending_server_verification: true,
          pending_data_url,
          rostros_cliente: faceCount,
          fallback_reason: e.message,
        });
        setStatus('preview');
      } catch (e2) {
        setError(e.message || e2.message);
        setStatus('error');
      }
    }
  };

  const confirmarGuardado = () => {
    if (!serverPreview) return;
    // Devuelve el staging_path (o pending_data_url si offline) al padre.
    // El /confirmar se ejecuta cuando el sync suba la observación con su obs_id real.
    onDone(serverPreview);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex flex-col" data-testid="anonimizar-modal">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide opacity-70">Anonimización</p>
          <h3 className="text-sm font-extrabold">Protección de identidad · rostros ocultos automáticamente</h3>
        </div>
        <button onClick={onCancel} data-testid="anonimizar-close" className="p-2 rounded-full hover:bg-white/10" aria-label="Cerrar">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-auto flex items-center justify-center p-3">
        <div className="relative max-w-full">
          <canvas
            ref={canvasRef}
            className="max-w-full max-h-[70vh] bg-white rounded-xl shadow-2xl touch-none cursor-crosshair"
            data-testid="anonimizar-canvas"
            onMouseDown={status === 'brushing' ? startBrush : undefined}
            onMouseMove={status === 'brushing' ? moveBrush : undefined}
            onMouseUp={endBrush}
            onMouseLeave={endBrush}
            onTouchStart={status === 'brushing' ? startBrush : undefined}
            onTouchMove={status === 'brushing' ? moveBrush : undefined}
            onTouchEnd={endBrush}
          />
          {status === 'loading' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 rounded-xl text-white text-sm gap-2">
              <Loader2 className="w-6 h-6 animate-spin" /> Detectando rostros y aplicando anonimización…
            </div>
          )}
          {status === 'uploading' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 rounded-xl text-white text-sm gap-2" data-testid="anonimizar-uploading">
              <Loader2 className="w-6 h-6 animate-spin" /> Enviando al servidor para validación adicional…
            </div>
          )}
          {status === 'preview' && serverPreview && (
            <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center p-6 text-white text-center rounded-xl" data-testid="anonimizar-server-preview">
              {serverPreview.pending_server_verification
                ? <AlertTriangle className="w-8 h-8 text-amber-400" />
                : (serverPreview.repixelado_servidor
                    ? <AlertTriangle className="w-8 h-8 text-amber-400" />
                    : <Check className="w-8 h-8 text-emerald-400" />)}
              <p className="mt-2 text-sm font-bold">
                {serverPreview.pending_server_verification
                  ? 'Sin conexión: la validación adicional del servidor se realizará al sincronizar. La imagen ya está pixelada en el dispositivo.'
                  : (serverPreview.repixelado_servidor
                      ? `El servidor detectó ${serverPreview.rostros_servidor} rostro(s) adicional(es) y aplicó pixelado extra`
                      : 'Validación del servidor completada sin hallazgos adicionales')}
              </p>
              {serverPreview.preview_url && (
                <img src={serverPreview.preview_url} alt="preview servidor" className="mt-3 max-h-[50vh] rounded-lg shadow-2xl" />
              )}
              {!serverPreview.preview_url && serverPreview.pending_data_url && (
                <img src={serverPreview.pending_data_url} alt="preview local" className="mt-3 max-h-[50vh] rounded-lg shadow-2xl" />
              )}
              <div className="mt-4 flex gap-2 flex-wrap justify-center">
                <button onClick={confirmarGuardado} data-testid="anonimizar-btn-confirmar-guardado"
                        className="px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold">
                  Confirmar y guardar
                </button>
                <button onClick={onCancel} data-testid="anonimizar-btn-cancelar-preview"
                        className="px-5 py-2.5 rounded-xl border border-white/40 text-white text-sm font-bold hover:bg-white/10">
                  Volver a tomar foto
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {status !== 'preview' && status !== 'error' && (
        <div className="border-t border-white/10 bg-black/70 px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-white text-xs">
          <div className="flex items-center gap-3">
            <span data-testid="anonimizar-rostros-count" className="px-2 py-1 rounded-full bg-white/10 font-bold">
              🎭 Rostros anonimizados: {faceCount}
            </span>
            {status === 'brushing' && (
              <>
                <span className="opacity-80">Pincel {manualBrushSize}px</span>
                <input type="range" min="16" max="120" step="4" value={manualBrushSize}
                       onChange={(e) => setManualBrushSize(Number(e.target.value))}
                       data-testid="anonimizar-brush-size" className="w-28" />
              </>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {status === 'ready' && (
              <button onClick={() => setStatus('brushing')} data-testid="anonimizar-btn-manual"
                      className="px-3 py-2 rounded-xl border border-white/40 text-white text-xs font-bold hover:bg-white/10 flex items-center gap-1">
                <Brush className="w-4 h-4" /> Ocultar zona manualmente
              </button>
            )}
            {status === 'brushing' && (
              <button onClick={() => setStatus('ready')} data-testid="anonimizar-btn-manual-done"
                      className="px-3 py-2 rounded-xl border border-white/40 text-white text-xs font-bold hover:bg-white/10">
                Terminar pincel
              </button>
            )}
            <button onClick={onCancel} data-testid="anonimizar-btn-retomar"
                    className="px-3 py-2 rounded-xl border border-white/40 text-white text-xs font-bold hover:bg-white/10 flex items-center gap-1">
              <RotateCcw className="w-4 h-4" /> Volver a tomar foto
            </button>
            <button onClick={confirmar} disabled={status !== 'ready' && status !== 'brushing'}
                    data-testid="anonimizar-btn-confirmar"
                    className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-60">
              <Check className="w-4 h-4" /> Confirmar y validar en servidor
            </button>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="border-t border-red-500/40 bg-red-900/40 px-4 py-4 text-white text-sm" data-testid="anonimizar-error">
          <p className="font-bold">No se pudo procesar la imagen</p>
          <p className="opacity-90 mt-1">{error}</p>
          <button onClick={onCancel} className="mt-3 px-4 py-2 rounded-xl border border-white/40 text-xs font-bold hover:bg-white/10">
            Volver
          </button>
        </div>
      )}
    </div>
  );
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

function fitSize(img, maxSide) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const scale = Math.min(1, maxSide / Math.max(iw, ih));
  return { w: Math.round(iw * scale), h: Math.round(ih * scale) };
}

function canvasToBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9));
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}
